import type { Menu, MenuItem, OptionGroup } from '@/types';
import { catalogName } from '@/lib/localizeCatalog';

/**
 * DF-0107 — traduire le refus du devis en une phrase qui nomme le plat.
 *
 * Le motif exact voyage jusqu'au navigateur et n'était lu nulle part :
 * `resource_api` renvoie `{"detail": "Option group below min_select (0 < 1): <uuid>"}`,
 * `sale_api` l'enveloppe tel quel dans `{"quote": {"detail": "…"}}`
 * (`views_online.py`, `raise serializers.ValidationError({"quote": detail})`),
 * et le gestionnaire d'erreur du carnet ne lisait que `data.code` et
 * `data.detail` — jamais `data.quote.detail`, qui est un objet imbriqué. Un
 * `grep -rn 'quote\.detail\|data\.quote'` sur `src/` ne rendait AUCUNE
 * occurrence dans tout le dépôt : n'importe quel refus de catalogue arrivait à
 * l'écran sous « Une erreur est survenue ».
 *
 * Mesuré chez Yuji le 2026-09-02 à 21:12 : une option obligatoire créée en
 * plein service (groupe « REMIS QUAND? », `min_select=1`) a fait refuser
 * 9 envois en 50 s sur la table S10. Le serveur ne pouvait pas savoir quelle
 * ligne était en cause ; il a rechargé la page et tâtonné plat par plat.
 *
 * Deux règles de conception :
 *
 * 1. **On ne renvoie jamais `null` quand un motif existe.** Le repli est le
 *    motif brut. Un message technique en anglais est laid, mais il nomme un
 *    uuid que le support peut chercher — « Une erreur est survenue » ne nomme
 *    rien du tout. C'est ce qui rend le critère de recette mesurable :
 *    zéro `errors.generic` pour une réponse portant un `quote.detail`.
 * 2. **Aucune traduction ici.** On rend une clé i18n + ses paramètres, le
 *    composant appelle `t()`. Le carnet du serveur tourne en 5 langues, et ce
 *    module est utilisable tel quel par le tunnel client (`CartDrawer`), qui a
 *    exactement le même trou.
 */

/** Ce qu'il faut afficher : une clé i18n et ses paramètres. */
export interface QuoteRefusal {
  key: string;
  params?: Record<string, string>;
}

/** Une ligne du panier, réduite à ce dont ce module a besoin. */
export interface RefusalLine {
  productId: string;
}

// Les identifiants sont OPAQUES : `order_quote.py` les interpole tels quels en
// fin de message. Ne pas exiger une forme d'uuid — un identifiant qui ne s'y
// conforme pas ferait retomber le message sur le repli brut, sans qu'aucun
// test ne le voie tant que les fixtures sont des uuids.
const ID = '\\S+';

/** Le motif brut, quand la réponse en porte un. `null` sinon. */
export function quoteDetailOf(error: unknown): string | null {
  const data = (error as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object') return null;
  const quote = (data as { quote?: unknown }).quote;
  // `{"quote": {"detail": "…"}}` — la forme normale (resource_api a répondu).
  if (quote && typeof quote === 'object') {
    const detail = (quote as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
  }
  // `{"quote": "Quote service unavailable: …"}` — resource_api injoignable ;
  // `views_online.py` met alors une CHAÎNE, pas un objet.
  if (typeof quote === 'string' && quote.trim()) return quote.trim();
  return null;
}

/** Tous les groupes d'options d'un plat, dans la forme que l'écran affiche. */
function groupsOf(item: MenuItem | undefined, menu: Menu | undefined): OptionGroup[] {
  if (!item) return [];
  // `optionGroups` porte les surcharges par produit (SC-93) ; `optionGroupIds`
  // est le repli des menus construits à la main dans les tests.
  if (item.optionGroups?.length) return item.optionGroups;
  const byId = new Map((menu?.optionGroups ?? []).map((g) => [g.id, g]));
  return item.optionGroupIds.map((id) => byId.get(id)).filter((g): g is OptionGroup => Boolean(g));
}

/** Le plat du panier qui porte ce groupe d'options, et le groupe lui-même. */
function findGroup(
  groupId: string,
  menu: Menu | undefined,
  lines: readonly RefusalLine[],
): { dish?: string; group?: string } {
  const byProduct = new Map((menu?.items ?? []).map((i) => [i.id, i]));
  for (const line of lines) {
    const item = byProduct.get(line.productId);
    const group = groupsOf(item, menu).find((g) => g.id === groupId);
    if (group) return { dish: item && catalogName(item), group: catalogName(group) };
  }
  // Le groupe peut avoir été créé APRÈS le chargement du menu de cet onglet —
  // c'est précisément le scénario DF-0106. On sait alors nommer le plat sans
  // savoir nommer le groupe, ou ni l'un ni l'autre.
  const cartOption = (menu?.cartOptionGroups ?? []).find((g) => g.id === groupId);
  return cartOption ? { group: catalogName(cartOption) } : {};
}

/** Le nom d'un plat du panier depuis son uuid produit. */
function dishName(productId: string, menu: Menu | undefined): string | undefined {
  const item = (menu?.items ?? []).find((i) => i.id === productId);
  return item && catalogName(item);
}

/** Le libellé d'une option, cherché dans tous les groupes du menu. */
function optionLabel(optionId: string, menu: Menu | undefined): string | undefined {
  const groups = [
    ...(menu?.optionGroups ?? []),
    ...(menu?.cartOptionGroups ?? []),
    ...(menu?.items ?? []).flatMap((i) => i.optionGroups ?? []),
  ];
  for (const group of groups) {
    const option = group.options.find((o) => o.id === optionId);
    if (option) return catalogName(option);
  }
  return undefined;
}

/**
 * Le refus, en une clé i18n. `null` uniquement quand la réponse ne porte
 * aucun motif — jamais parce qu'on n'a pas su le reconnaître.
 */
export function describeQuoteRefusal(
  error: unknown,
  menu: Menu | undefined,
  lines: readonly RefusalLine[],
): QuoteRefusal | null {
  // `total_mismatch` is NOT a `quote.detail` reason: `views_online.py`
  // answers `{"detail": "total_mismatch", "quote": <full quote, no .detail>}`
  // at the TOP level, before the quote is even checked against a catalogue
  // refusal — `quoteDetailOf` never sees it. The one case measured in the
  // field is a dish's price moving since the line was added.
  const data = (error as { data?: unknown } | undefined)?.data;
  if (data && typeof data === 'object' && (data as { detail?: unknown }).detail === 'total_mismatch') {
    return { key: 'waiter.ticket.confirm.errors.priceChanged' };
  }

  const detail = quoteDetailOf(error);
  if (!detail) return null;

  // Le cas mesuré : un groupe obligatoire sans réponse. `order_quote.py:302`
  // renvoie l'uuid du GROUPE, d'où la recherche par groupe et non par produit.
  const belowMin = detail.match(new RegExp(`^Option group below min_select \\([^)]*\\): (${ID})`));
  if (belowMin) {
    const { dish, group } = findGroup(belowMin[1], menu, lines);
    if (dish && group) return { key: 'waiter.ticket.confirm.errors.optionMissingOn', params: { group, dish } };
    if (group) return { key: 'waiter.ticket.confirm.errors.optionMissing', params: { group } };
    return { key: 'waiter.ticket.confirm.errors.optionMissingUnknown' };
  }

  // DF-0161 — un groupe OBLIGATOIRE desactive alors qu'il est encore rattache
  // au plat. `order_quote.py` nomme le PRODUIT et non le groupe : le groupe
  // inactif est absent du menu servi, `findGroup` ne saurait rien en dire.
  const groupInactive = detail.match(
    new RegExp(`^Required option group inactive \\([^)]*\\) for product: (${ID})`),
  );
  if (groupInactive) {
    const dish = dishName(groupInactive[1], menu);
    return dish
      ? { key: 'waiter.ticket.confirm.errors.optionGroupInactiveNamed', params: { dish } }
      : { key: 'waiter.ticket.confirm.errors.optionGroupInactive' };
  }

  if (/^Option group exceeds max_select/.test(detail)) {
    return { key: 'waiter.ticket.confirm.errors.optionTooMany' };
  }

  const unknownProduct = detail.match(new RegExp(`^Unknown or inactive product: (${ID})`));
  if (unknownProduct) {
    const dish = dishName(unknownProduct[1], menu);
    return dish
      ? { key: 'waiter.ticket.confirm.errors.dishUnavailableNamed', params: { dish } }
      : { key: 'waiter.ticket.confirm.errors.dishUnavailable' };
  }

  const badOption = detail.match(
    new RegExp(
      `^(?:Unknown or inactive option|Inactive option for product|Option does not belong to product|Unknown or inactive cart option|Cart option not available for this sale mode): (${ID})`,
    ),
  );
  if (badOption) {
    const option = optionLabel(badOption[1], menu);
    return option
      ? { key: 'waiter.ticket.confirm.errors.optionUnavailableNamed', params: { option } }
      : { key: 'waiter.ticket.confirm.errors.optionUnavailable' };
  }

  const optionQty = detail.match(
    new RegExp(`^(?:Option|Cart option) qty (?:below min_quantity|exceeds max_quantity) \\([^)]*\\): (${ID})`),
  );
  if (optionQty) {
    const option = optionLabel(optionQty[1], menu);
    return option
      ? { key: 'waiter.ticket.confirm.errors.optionQtyNamed', params: { option } }
      : { key: 'waiter.ticket.confirm.errors.optionQty' };
  }

  if (/^Quote service unavailable/.test(detail)) {
    return { key: 'waiter.ticket.confirm.errors.quoteUnavailable' };
  }

  // Repli délibéré : le motif brut plutôt que « Une erreur est survenue ».
  // Il est en anglais et porte un uuid, mais il dit QUELQUE CHOSE — et c'est
  // ce qui fait tomber le compteur `errors.generic` à zéro.
  return { key: 'waiter.ticket.confirm.errors.quoteRaw', params: { detail } };
}
